import React, { useEffect, useMemo, useState } from "react";
import {
  DIET_TYPES,
  DISINTEGRATION_TYPES,
  RELEASE_TYPES,
  ROUTES,
  SUGAR_STATUSES,
  TARGET_GROUPS,
  formStyles,
} from "../../../components/Inventory-Stocks & Orders/Admin/Products/catalogConstants";
import { ProductCatalogService } from "../../../services/api/productCatalog.service";

interface ProductAttributesTabProps {
  data: any;
  onChange: (field: string, value: any) => void;
  isEdit: boolean;
  onUpdate?: () => void;
  isLoading?: boolean;
}

interface FormOption {
  id: string;
  name: string;
  formGroupId: string;
  formGroupName: string | null;
}

/**
 * Attributes, branching on `isMedicine`.
 *
 * A medicine and a non-medicine product carry genuinely different attributes
 * and they live in different tables - medicine_attributes versus
 * product_attributes - so only one set is shown and only that set is sent.
 */
export const ProductAttributesTab: React.FC<ProductAttributesTabProps> = ({
  data,
  onChange,
  isEdit,
  onUpdate,
  isLoading,
}) => {
  const [formOptions, setFormOptions] = useState<FormOption[]>([]);
  const [isLoadingForms, setIsLoadingForms] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const res = await ProductCatalogService.getFormOptions({ isActive: true });
      if (cancelled) return;
      setFormOptions(res?.success ? res.data || [] : []);
      setIsLoadingForms(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Forms grouped by their form group, so a long flat list stays navigable. */
  const groupedForms = useMemo(() => {
    const groups = new Map<string, FormOption[]>();
    formOptions.forEach((option) => {
      const key = option.formGroupName || "Ungrouped";
      groups.set(key, [...(groups.get(key) || []), option]);
    });
    return Array.from(groups.entries());
  }, [formOptions]);

  const formSelect = (field: string, value: string) => (
    <select
      value={value}
      onChange={(e) => onChange(field, e.target.value)}
      className={formStyles.inputStyle}
    >
      <option value="">
        {isLoadingForms ? "Loading forms..." : "Select Product Form"}
      </option>
      {groupedForms.map(([groupName, options]) => (
        <optgroup key={groupName} label={groupName}>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );

  return (
    <div className="space-y-6">
      {data.isMedicine ? (
        <section className={formStyles.sectionCard}>
          <h4 className={`${formStyles.sectionTitle} mb-1`}>
            Medicine Attributes
          </h4>
          <p className="text-xs text-gray-500 mb-4">
            How the medicine is formulated and administered.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={formStyles.lableStyle}>Product Form</label>
              {formSelect("medProductFormId", data.medProductFormId)}
            </div>

            <div>
              <label className={formStyles.lableStyle}>
                Route of Administration
              </label>
              <select
                value={data.route}
                onChange={(e) => onChange("route", e.target.value)}
                className={formStyles.inputStyle}
              >
                <option value="">Select route</option>
                {ROUTES.map((route) => (
                  <option key={route} value={route}>
                    {route}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={formStyles.lableStyle}>Release Type</label>
              <select
                value={data.releaseType}
                onChange={(e) => onChange("releaseType", e.target.value)}
                className={formStyles.inputStyle}
              >
                <option value="">Select release type</option>
                {RELEASE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                IR immediate, SR sustained, ER extended, and so on.
              </p>
            </div>

            <div>
              <label className={formStyles.lableStyle}>
                Disintegration Type
              </label>
              <select
                value={data.disintegrationType}
                onChange={(e) => onChange("disintegrationType", e.target.value)}
                className={formStyles.inputStyle}
              >
                <option value="">Select disintegration type</option>
                {DISINTEGRATION_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>
      ) : (
        <section className={formStyles.sectionCard}>
          <h4 className={`${formStyles.sectionTitle} mb-1`}>
            Product Attributes
          </h4>
          <p className="text-xs text-gray-500 mb-4">
            Variant, dietary and audience details for non-medicine products.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={formStyles.lableStyle}>Product Form</label>
              {formSelect("prodProductFormId", data.prodProductFormId)}
            </div>

            <div>
              <label className={formStyles.lableStyle}>Variant</label>
              <input
                type="text"
                value={data.variant}
                onChange={(e) => onChange("variant", e.target.value)}
                className={formStyles.inputStyle}
                placeholder="e.g. 500 g refill pack"
              />
            </div>

            <div>
              <label className={formStyles.lableStyle}>Flavour</label>
              <input
                type="text"
                value={data.flavour}
                onChange={(e) => onChange("flavour", e.target.value)}
                className={formStyles.inputStyle}
                placeholder="e.g. Chocolate"
              />
            </div>

            <div>
              <label className={formStyles.lableStyle}>Target Group</label>
              <input
                type="text"
                list="target-groups"
                value={data.targetGroup}
                onChange={(e) => onChange("targetGroup", e.target.value)}
                className={formStyles.inputStyle}
                placeholder="e.g. Adult"
              />
              <datalist id="target-groups">
                {TARGET_GROUPS.map((group) => (
                  <option key={group} value={group} />
                ))}
              </datalist>
            </div>

            <div>
              <label className={formStyles.lableStyle}>
                Minimum Age (months)
              </label>
              <input
                type="number"
                min={0}
                value={data.ageMinMonths}
                onChange={(e) => onChange("ageMinMonths", e.target.value)}
                className={formStyles.inputStyle}
                placeholder="e.g. 24"
              />
            </div>

            <div>
              <label className={formStyles.lableStyle}>
                Maximum Age (months)
              </label>
              <input
                type="number"
                min={0}
                value={data.ageMaxMonths}
                onChange={(e) => onChange("ageMaxMonths", e.target.value)}
                className={formStyles.inputStyle}
                placeholder="Leave blank for no upper limit"
              />
            </div>

            <div>
              <label className={formStyles.lableStyle}>Diet Type</label>
              <select
                value={data.dietType}
                onChange={(e) => onChange("dietType", e.target.value)}
                className={formStyles.inputStyle}
              >
                <option value="">Select diet type</option>
                {DIET_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={formStyles.lableStyle}>Sugar Status</label>
              <select
                value={data.sugarStatus}
                onChange={(e) => onChange("sugarStatus", e.target.value)}
                className={formStyles.inputStyle}
              >
                <option value="">Select sugar status</option>
                {SUGAR_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className={formStyles.lableStyle}>Key Ingredients</label>
              <textarea
                value={data.attrKeyIngredients}
                onChange={(e) => onChange("attrKeyIngredients", e.target.value)}
                className={`${formStyles.inputStyle} h-20 resize-none`}
                placeholder="Main ingredients, as listed on the pack"
              />
            </div>
          </div>
        </section>
      )}

      {isEdit && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onUpdate}
            disabled={isLoading}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition disabled:opacity-50"
          >
            {isLoading ? "Updating..." : "Update Attributes"}
          </button>
        </div>
      )}
    </div>
  );
};
